import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  PASSWORD_MAX,
  PASSWORD_MIN,
  USER_STATUS_LABEL,
  UserStatus,
  type UserSummary,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { userApi } from '../api/user.api';
import { useAuth } from '../hooks/useAuth';
import { SidePanel } from '../components/SidePanel';
import { UserForm } from '../components/UserForm';

/**
 * PP-01 - the people list, with PP-03 (add a person) in a side panel.
 */

const STATUS_STYLE: Record<UserStatus, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  INVITED: 'bg-amber-50 text-amber-700 ring-amber-200',
  DEACTIVATED: 'bg-slate-100 text-slate-500 ring-slate-200',
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The admin's reset dialog: set a new temporary password and pass it on.
 * A dialog rather than a panel - it is one field, and it should feel like a
 * deliberate, contained act.
 */
function ResetPasswordDialog({
  user,
  onDone,
  onCancel,
}: {
  user: UserSummary;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      setError(`Between ${PASSWORD_MIN} and ${PASSWORD_MAX} characters`);
      return;
    }
    setBusy(true);
    try {
      const mail = await userApi.resetPassword(user.id, { password });
      /* The password is shown nowhere again, so whether the person was
         actually emailed decides what the admin has to do next. */
      onDone(
        mail === 'sent'
          ? `Password reset for ${user.displayName} and emailed to them.`
          : mail === 'skipped'
            ? `Password reset for ${user.displayName}. No email was sent`
              + ' (email is off, muted, or they have no address on file) -'
              + ' pass the new one on, it is shown nowhere again.'
            : `Password reset for ${user.displayName}, but the email failed.`
              + ' Pass the new one on - it is shown nowhere again.',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reset the password');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-slate-900/40 p-4">
      <form onSubmit={submit}
            className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
        <h2 className="text-base font-semibold text-slate-900">Reset password</h2>
        <p className="mt-1 text-sm text-slate-500">
          Set a new temporary password for <strong>{user.displayName}</strong>.
          It is emailed to them if they have an address on file - you will be
          told if it could not be sent.
        </p>
        <input
          type="text"
          autoFocus
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(null); }}
          placeholder="New temporary password"
          className={[
            'mt-4 w-full rounded-lg border px-3 py-2 font-mono text-sm outline-none transition focus:ring-2',
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
          ].join(' ')}
        />
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-60">
            {busy ? 'Saving...' : 'Set password'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function PeoplePage() {
  const { user: me } = useAuth();

  /** SUPER_ADMIN > ADMIN > everyone - mirror of the server's hierarchy rule. */
  const RANK: Record<string, number> = { SUPER_ADMIN: 3, ADMIN: 2 };
  const myRank = RANK[me?.role.systemKey ?? ''] ?? 1;
  const outranksMe = (person: UserSummary) =>
    person.id !== me?.id && (RANK[person.roleSystemKey ?? ''] ?? 1) > myRank;
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [resetting, setResetting] = useState<UserSummary | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userApi.list({
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(status ? { status } : {}),
        pageSize: 50,
      });
      setUsers(response.data);
      setTotal(response.meta?.total ?? response.data.length);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not load the people list',
      );
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  /** Debounced: without it every keystroke fires a request. */
  useEffect(() => {
    const timer = setTimeout(() => void load(), 250);
    return () => clearTimeout(timer);
  }, [load]);

  async function remove(user: UserSummary) {
    setBusyId(user.id);
    try {
      await userApi.remove(user.id);
      setFlash(`${user.displayName} removed. Their tasks and history keep their name.`);
      void load();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not remove this person');
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  }

  function handleCreated(user: UserSummary, warning?: string) {
    setPanelOpen(false);
    setFlash(warning ?? `${user.displayName} added.`);
    // Refetch rather than pushing the new row in: the list is filtered and
    // sorted server-side, so guessing where it belongs would be wrong as
    // often as right.
    void load();
  }

  return (
    <div className="mx-auto  px-8 py-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">People</h1>
          <p className="mt-1 text-sm text-slate-500">
            {loading ? 'Loading...' : `${total} ${total === 1 ? 'person' : 'people'}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          Add person
        </button>
      </header>

      {flash && (
        <div
          role="status"
          className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200"
        >
          <span>{flash}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            className="text-emerald-600 hover:text-emerald-800"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, email or employee ID"
          className="w-64 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
        {(['', ...Object.values(UserStatus)] as const).map((value) => (
          <button
            key={value || 'all'}
            type="button"
            onClick={() => setStatus(value)}
            className={[
              'rounded-full px-3 py-1.5 text-xs font-medium ring-1 transition',
              status === value
                ? 'bg-slate-900 text-white ring-slate-900'
                : 'bg-white text-slate-600 ring-slate-300 hover:bg-slate-50',
            ].join(' ')}
          >
            {value ? USER_STATUS_LABEL[value] : 'All'}
          </button>
        ))}
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {error ? (
          <div className="px-5 py-12 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
            >
              Try again
            </button>
          </div>
        ) : loading ? (
          <ul className="divide-y divide-slate-100">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="flex items-center gap-3 px-5 py-3.5">
                <div className="h-9 w-9 animate-pulse rounded-full bg-slate-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-40 animate-pulse rounded bg-slate-100" />
                  <div className="h-2.5 w-24 animate-pulse rounded bg-slate-100" />
                </div>
              </li>
            ))}
          </ul>
        ) : users.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <p className="text-sm font-medium text-slate-900">No people found</p>
            <p className="mt-1 text-sm text-slate-500">
              {search || status
                ? 'Try clearing the filters.'
                : 'Add the first person to get started.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Name</th>
                <th className="px-5 py-2.5 font-medium">Employee ID</th>
                <th className="px-5 py-2.5 font-medium">Role</th>
                <th className="px-5 py-2.5 font-medium">Department</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
                <th className="px-5 py-2.5 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user) => (
                <tr key={user.id} className="transition hover:bg-slate-50/70">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt=""
                          className="h-9 w-9 rounded-full object-cover"
                        />
                      ) : (
                        <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                          {initials(user.displayName)}
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900">
                          {user.displayName}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          {user.email ?? user.jobTitle ?? ''}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {/* Tabular numerals so the column reads as a list of
                        codes rather than ragged text. */}
                    <span className="font-mono text-xs text-slate-600">
                      {user.employeeId ?? '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{user.roleName}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {user.departmentName ?? '—'}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${STATUS_STYLE[user.status]}`}
                    >
                      {USER_STATUS_LABEL[user.status]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {confirmDeleteId === user.id ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <button type="button" disabled={busyId === user.id}
                          onClick={() => void remove(user)}
                          className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50">
                          {busyId === user.id ? 'Removing...' : 'Confirm'}
                        </button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                          Keep
                        </button>
                      </span>
                    ) : outranksMe(user) ? (
                      <span className="text-xs text-slate-300" title="Managed by a higher role">
                        —
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <button type="button" onClick={() => setEditing(user)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                          Edit
                        </button>
                        <button type="button" onClick={() => setResetting(user)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                          Reset password
                        </button>
                        {user.id !== me?.id && (
                          <button type="button" onClick={() => setConfirmDeleteId(user.id)}
                            className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50">
                            Delete
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SidePanel
        open={panelOpen}
        title="Add person"
        description="They will sign in with their email and the password you set here."
        onClose={() => setPanelOpen(false)}
      >
        <UserForm onCreated={handleCreated} onCancel={() => setPanelOpen(false)} />
      </SidePanel>

      {editing && (
        <SidePanel
          open
          title={`Edit ${editing.displayName}`}
          description="Only what you change is saved."
          onClose={() => setEditing(null)}
        >
          <UserForm
            user={editing}
            onCreated={(saved, warning) => {
              setEditing(null);
              setFlash(warning ?? `${saved.displayName} updated.`);
              void load();
            }}
            onCancel={() => setEditing(null)}
          />
        </SidePanel>
      )}

      {resetting && (
        <ResetPasswordDialog
          user={resetting}
          onDone={(message) => { setResetting(null); setFlash(message); }}
          onCancel={() => setResetting(null)}
        />
      )}
    </div>
  );
}
